import { useState } from "react";
import { Loader2, Users, Shield } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useGroupStore } from "@/stores/groupStore";
import { getApiErrorMessage } from "@/stores/authStore";

interface JoinGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function parseInviteUrl(raw: string): { uuid: string; token: string } | null {
  try {
    const url = new URL(raw.trim());
    // Legacy format: /groups/:uuid/join?token=...
    const legacyMatch = url.pathname.match(/\/groups\/([^/]+)\/join/);
    const legacyToken = url.searchParams.get("token");
    if (legacyMatch && legacyToken) return { uuid: legacyMatch[1], token: legacyToken };
    // New format: /join/:token
    const newMatch = url.pathname.match(/\/join\/([^/]+)/);
    if (newMatch) return { uuid: "", token: newMatch[1] };
  } catch {
    // not a URL
  }
  return null;
}

export function JoinGroupDialog({ open, onOpenChange }: JoinGroupDialogProps) {
  const { joinGroup, joinViaLink, joinViaCode } = useGroupStore();

  const [tab, setTab] = useState<"url" | "code">("url");

  // URL flow
  const [inviteUrl, setInviteUrl] = useState("");
  const [urlError, setUrlError] = useState("");

  // Code flow
  const [inviteCode, setInviteCode] = useState("");
  const [codeError, setCodeError] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const handleClose = (newOpen: boolean) => {
    if (!newOpen) {
      setInviteUrl("");
      setInviteCode("");
      setUrlError("");
      setCodeError("");
      setTab("url");
    }
    onOpenChange(newOpen);
  };

  const handleJoinByUrl = async () => {
    if (!inviteUrl.trim()) {
      setUrlError("Invite link is required.");
      return;
    }
    const parsed = parseInviteUrl(inviteUrl);
    if (!parsed) {
      setUrlError("Please paste a valid KashRound invite link.");
      return;
    }
    setIsLoading(true);
    try {
      if (parsed.uuid) {
        await joinGroup(parsed.uuid, parsed.token);
      } else {
        await joinViaLink(parsed.token);
      }
      toast.success("You've joined the group!");
      handleClose(false);
    } catch (err) {
      setUrlError(getApiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinByCode = async () => {
    const code = inviteCode.trim().toUpperCase();
    if (!code) {
      setCodeError("Invite code is required.");
      return;
    }
    if (code.length !== 8) {
      setCodeError("Invite codes are exactly 8 characters (e.g. KR4X9MBW).");
      return;
    }
    setIsLoading(true);
    try {
      await joinViaCode(code);
      toast.success("You've joined the group!");
      handleClose(false);
    } catch (err) {
      setCodeError(getApiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Join a Group</DialogTitle>
          <DialogDescription>
            Paste an invite link or enter the 8-character invite code shared by a group admin.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "url" | "code")}>
          <TabsList className="w-full">
            <TabsTrigger value="url" className="flex-1">Paste Link</TabsTrigger>
            <TabsTrigger value="code" className="flex-1">Enter Code</TabsTrigger>
          </TabsList>

          <TabsContent value="url" className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-url" required>Invite link</Label>
              <Input
                id="invite-url"
                placeholder="https://kashround.app/join/..."
                value={inviteUrl}
                onChange={(e) => { setInviteUrl(e.target.value); setUrlError(""); }}
                aria-invalid={!!urlError}
              />
              {urlError ? (
                <p className="text-xs text-destructive">{urlError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Paste the full invitation URL shared with you</p>
              )}
            </div>

            <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <Users className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Join existing members</p>
                  <p className="text-xs text-muted-foreground">Connect with your group instantly</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary/10">
                  <Shield className="h-4 w-4 text-secondary" />
                </div>
                <div>
                  <p className="font-medium">Secure & verified</p>
                  <p className="text-xs text-muted-foreground">Invitation links are encrypted and time-limited</p>
                </div>
              </div>
            </div>

            <Button
              variant="accent"
              className="w-full"
              onClick={handleJoinByUrl}
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Join Group
            </Button>
          </TabsContent>

          <TabsContent value="code" className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-code" required>Invite code</Label>
              <Input
                id="invite-code"
                placeholder="KR4X9MBW"
                value={inviteCode}
                maxLength={8}
                className="font-mono uppercase tracking-widest"
                aria-invalid={!!codeError}
                onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setCodeError(""); }}
              />
              {codeError ? (
                <p className="text-xs text-destructive">{codeError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Enter the 8-character code shared by a group admin</p>
              )}
            </div>

            <Button
              variant="accent"
              className="w-full"
              onClick={handleJoinByCode}
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Join Group
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
